import { Injectable } from "@angular/core";
import { EstadoBorradorWizardEmisor } from "../models/Emisor/estado-borrador-wizard-emisor";
import { LocalStorageService } from "./local-storage.service";

interface CargaBorradorCifrada {
	iv: string;
	data: string;
}

@Injectable({
	providedIn: "root",
})
export class BorradorWizardEmisorService {
	private readonly textEncoder = new TextEncoder();
	private readonly textDecoder = new TextDecoder();
	private readonly salt = "wizard-emisor";

	constructor(private localStorage: LocalStorageService) {}

	async guardarBorrador(borrador: EstadoBorradorWizardEmisor, userId: string): Promise<void> {
		const textoPlano = JSON.stringify(borrador);
		const payload = await this.cifrarSiEsPosible(textoPlano, userId);
		const valorActual = this.localStorage.obtenerBorradorWizardEmisor(userId);
		if (valorActual && this.esBorradorPlanoMasReciente(valorActual, borrador)) {
			return;
		}
		this.localStorage.guardarBorradorWizardEmisor(userId, payload);
	}

	guardarBorradorInmediato(borrador: EstadoBorradorWizardEmisor, userId: string): void {
		this.localStorage.guardarBorradorWizardEmisor(userId, JSON.stringify(borrador));
	}

	async obtenerBorrador(userId: string): Promise<EstadoBorradorWizardEmisor | null> {
		const valor = this.localStorage.obtenerBorradorWizardEmisor(userId);
		if (!valor) {
			return null;
		}

		try {
			const textoPlano = await this.descifrarSiCorresponde(valor, userId);
			return JSON.parse(textoPlano) as EstadoBorradorWizardEmisor;
		} catch (error) {
			this.limpiarBorrador(userId);
			return null;
		}
	}

	limpiarBorrador(userId: string): void {
		this.localStorage.eliminarBorradorWizardEmisor(userId);
	}

	private async cifrarSiEsPosible(value: string, userId: string): Promise<string> {
		if (!window.crypto?.subtle) {
			return value;
		}
		try {
			const llave = await this.obtenerLlave(userId);
			const iv = window.crypto.getRandomValues(new Uint8Array(12));
			const data = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, llave, this.textEncoder.encode(value));
			return JSON.stringify({ iv: this.arrayBufferABase64(iv.buffer), data: this.arrayBufferABase64(data) } as CargaBorradorCifrada);
		} catch (error) {
			return value;
		}
	}

	private async descifrarSiCorresponde(value: string, userId: string): Promise<string> {
		const payload = JSON.parse(value) as CargaBorradorCifrada;
		if (!this.esCargaCifrada(payload)) {
			return value;
		}
		if (!window.crypto?.subtle) {
			throw new Error("El navegador no puede descifrar el borrador");
		}
		const llave = await this.obtenerLlave(userId);
		const data = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: new Uint8Array(this.base64AArrayBuffer(payload.iv)) }, llave, this.base64AArrayBuffer(payload.data));
		return this.textDecoder.decode(data);
	}

	private obtenerLlave(userId: string): Promise<CryptoKey> {
		return window.crypto.subtle.importKey("raw", this.textEncoder.encode(userId), { name: "PBKDF2" }, false, ["deriveKey"])
			.then(material => window.crypto.subtle.deriveKey({ salt: this.textEncoder.encode(this.salt), iterations: 100000, hash: "SHA-256", name: "PBKDF2" }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]));
	}

	private esCargaCifrada(value: any): value is CargaBorradorCifrada {
		return value && typeof value === "object" && typeof value.iv === "string" && typeof value.data === "string";
	}

	private esBorradorPlanoMasReciente(payload: string, borrador: EstadoBorradorWizardEmisor): boolean {
		try {
			const actual = JSON.parse(payload) as EstadoBorradorWizardEmisor | CargaBorradorCifrada;
			if (this.esCargaCifrada(actual)) {
				return false;
			}
			return new Date(actual.actualizadoEn || 0).getTime() > new Date(borrador.actualizadoEn || 0).getTime();
		} catch (error) {
			return false;
		}
	}

	private arrayBufferABase64(buffer: ArrayBuffer): string {
		let binario = "";
		new Uint8Array(buffer).forEach(byte => binario += String.fromCharCode(byte));
		return window.btoa(binario);
	}

	private base64AArrayBuffer(base64: string): ArrayBuffer {
		const binario = window.atob(base64);
		const bytes = new Uint8Array(binario.length);
		for (let i = 0; i < binario.length; i++) {
			bytes[i] = binario.charCodeAt(i);
		}
		return bytes.buffer;
	}
}
